import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { HEADERS } from '../../../../API/UrlAndPaths';

// Custom hook to fetch and manage employee images
export const useEmployeeImages = () => {
  // Memoized image component to prevent re-renders
  const EmployeeImageComponent = useMemo(() => {
    // Inline SVG placeholder (40x40 circle) eliminates network 404 loops
    const FALLBACK_DATA_URL =
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <rect width="40" height="40" fill="#f0f0f0"/>
          <circle cx="20" cy="15" r="8" fill="#c0c0c0"/>
          <rect x="10" y="26" width="20" height="8" rx="4" fill="#d0d0d0"/>
        </svg>`
      );

    return ({ src, alt, size = 40 }) => (
      <img
        src={src || FALLBACK_DATA_URL}
        alt={alt}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #e0e0e0'
        }}
        onError={(e) => {
          // Prevent infinite onError loop by removing handler, then set fallback
          e.currentTarget.onerror = null;
          e.currentTarget.src = FALLBACK_DATA_URL;
        }}
      />
    );
  }, []);
  const [employeeImages, setEmployeeImages] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch all employees with their images
  const fetchEmployeeImages = async () => {
    try {
      setLoading(true);
      const res = await axios.get("employee/getAllEmployees", HEADERS);
      if (res.status === 200) {
        const employees = res.data?.data || res.data || [];
        const imageMap = {};
        
        employees.forEach(emp => {
          // Try multiple possible image fields
          const imageUrl = 
            (emp.imageUrl && emp.imageUrl !== 'http://192.168.1.55:3100/' ? emp.imageUrl : null) ||
            (emp.profileImage && emp.profileImage !== 'http://192.168.1.55:3100/' ? emp.profileImage : null) ||
            (emp.image && emp.image !== 'http://192.168.1.55:3100/' ? emp.image : null) ||
            (emp.employeePhoto && emp.employeePhoto !== 'http://192.168.1.55:3100/' ? emp.employeePhoto : null) ||
            (emp.photo && emp.photo !== 'http://192.168.1.55:3100/' ? emp.photo : null) ||
            null;
          
          // Map by employee ID and employee code for flexibility
          if (emp._id) {
            imageMap[emp._id] = imageUrl;
          }
          if (emp.employeeCode) {
            imageMap[emp.employeeCode] = imageUrl;
          }
          if (emp.empId) {
            imageMap[emp.empId] = imageUrl;
          }
        });
        
        console.log('Employee images loaded:', Object.keys(imageMap).length, 'employees');
        setEmployeeImages(imageMap);
      }
    } catch (error) {
      console.error('Error fetching employee images:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get image URL for a specific employee
  const getEmployeeImage = (employeeId, employeeCode, empId) => {
    // Try different identifiers to find the image
    return employeeImages[employeeId] || 
           employeeImages[employeeCode] || 
           employeeImages[empId] || 
           null;
  };

  // Enhanced EmployeeImage component that uses the fetched images
  const EmployeeImage = ({ employeeId, employeeCode, empId, employeeName, size = 40 }) => {
    const imageUrl = getEmployeeImage(employeeId, employeeCode, empId);
    return <EmployeeImageComponent src={imageUrl} alt={employeeName || 'Employee'} size={size} />;
  };

  useEffect(() => {
    fetchEmployeeImages();
  }, []);

  return {
    employeeImages,
    loading,
    EmployeeImage,
    fetchEmployeeImages,
    getEmployeeImage
  };
};

export default useEmployeeImages;
